import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchJson, formatMoney } from '@/lib/api';
import { Modal, Row, subirFoto } from './comunes';

// Por que la foto no se cobra, en las palabras del comerciante.
const SIN_CARGO = {
  already_paid_for_this_shipment: 'This shipment already has a photo: more photos are free.',
  waived: 'Photos are free on your account.',
  uploaded_by_staff: 'Stored by the office: no charge to the merchant.',
};

/**
 * La foto del articulo al despacharlo, para comparar si despues hay un reclamo.
 * Cuesta 1,99 por envio salvo exencion (Mike, 4-oct): el precio se muestra en
 * el boton antes de subirla, y sin precio no se sube.
 */
export default function ModalFoto({ poliza, token, onClose, onStored }) {
  const [archivo, setArchivo] = useState(null);
  const [serial, setSerial] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [precio, setPrecio] = useState(null);

  useEffect(() => {
    fetchJson(`/photo-evidence/coverage/${poliza.verification_number}/price`, { token })
      .then(setPrecio)
      .catch(e => setError(e.message));
  }, [poliza.verification_number, token]);

  const guardar = async () => {
    if (!archivo) { setError('Choose a photo first.'); return; }
    setBusy(true); setError('');
    try {
      const r = await subirFoto(`/photo-evidence/coverage/${poliza.verification_number}`, token, archivo, serial);
      const cobro = r.photo_fee?.charged ? ` · ${formatMoney(r.photo_fee.amount)} charged to your wallet` : '';
      onStored(`Item photo stored for ${poliza.verification_number}${cobro}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const conCargo = precio && precio.amount > 0;

  return (
    <Modal onClose={onClose} title={`Item photo for ${poliza.verification_number}`} testid="foto-modal">
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          A photo of the item as it is being shipped. Its value is that it
          exists before anything goes wrong, so it can be compared with a
          damage photo later.
        </p>
        <Row label="Photo">
          <input type="file" accept="image/jpeg,image/png,image/webp"
            onChange={e => setArchivo(e.target.files?.[0] || null)}
            className="w-full text-sm" data-testid="foto-file" />
        </Row>
        <Row label="Serial (optional)">
          <input value={serial} onChange={e => setSerial(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm font-mono"
            data-testid="foto-serial" />
        </Row>
        <p className="text-[11px] text-slate-500">
          The file is stored exactly as uploaded, with a SHA-256 of its
          bytes and the server time it arrived. The hash shows the file was
          not altered afterwards. It does not show that two photos are of
          the same item: that is for a person to look at.
        </p>
        {precio && (
          <p className="text-xs text-slate-600" data-testid="foto-price">
            {conCargo
              ? `The photo is optional and costs ${formatMoney(precio.amount)} per shipment, charged to your wallet once.`
              : SIN_CARGO[precio.reason] || 'No charge.'}
          </p>
        )}
        <Button onClick={guardar} disabled={busy || !archivo || !precio}
          className="w-full bg-gradient-to-r from-purple-500 to-indigo-500" data-testid="foto-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" />
            : !precio ? 'Getting the price…'
              : conCargo ? `Store photo · ${formatMoney(precio.amount)}` : 'Store photo'}
        </Button>
        {error && (
          <div className="p-2.5 rounded-lg bg-rose-50 text-rose-700 text-xs" data-testid="foto-error">{error}</div>
        )}
      </div>
    </Modal>
  );
}
